import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';

export type LoadingSkeletonVariant = 'card' | 'tile' | 'row';

export interface LoadingSkeletonProps {
  variant: LoadingSkeletonVariant;
  className?: string;
}

/**
 * `<LoadingSkeleton>` — animated glass placeholder.
 *
 * Variants (UI-SPEC § 3.10):
 * - `card` — full PropertyCard footprint (1100×330 max), inner `bg-ocean-800`
 *           shapes mimicking photo + heading + bars.
 * - `tile` — StatTile shape (h-24, label + value rows).
 * - `row`  — h-2 thin pulse for inline loading hints.
 *
 * Animation: Tailwind `animate-pulse`. The reduced-motion `@media` rule in
 * the global stylesheet zeroes animation duration, so the skeleton renders
 * statically for users who request reduced motion.
 */
export function LoadingSkeleton({ variant, className }: LoadingSkeletonProps) {
  if (variant === 'card') {
    return (
      <GlassPanel
        variant="base"
        aria-hidden
        className={cn(
          'w-full max-w-[1100px] h-[330px] grid grid-cols-1 md:grid-cols-[440px_1fr] gap-0 p-0 overflow-hidden animate-pulse',
          className,
        )}
      >
        <div className="bg-ocean-800/80 w-full h-[220px] md:h-full" />
        <div className="p-6 grid grid-rows-[auto_auto_1fr_auto] gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="h-5 bg-ocean-800/60 rounded w-2/3" />
            <div className="w-24 h-24 bg-ocean-800/60 rounded-2xl" />
          </div>
          <div className="grid gap-2">
            <div className="h-2 bg-ocean-800/60 rounded-full w-5/6" />
            <div className="h-2 bg-ocean-800/60 rounded-full w-4/6" />
            <div className="h-2 bg-ocean-800/60 rounded-full w-3/6" />
          </div>
          <div className="flex items-end gap-3">
            <div className="h-3 bg-ocean-800/60 rounded w-1/2" />
          </div>
          <div className="flex gap-2">
            <div className="h-6 bg-ocean-800/60 rounded-full w-20" />
            <div className="h-6 bg-ocean-800/60 rounded-full w-16" />
          </div>
        </div>
      </GlassPanel>
    );
  }
  if (variant === 'tile') {
    return (
      <GlassPanel
        variant="strong"
        aria-hidden
        className={cn(
          'h-24 p-5 grid grid-rows-[auto_auto] gap-2 items-start animate-pulse',
          className,
        )}
      >
        <div className="h-3 bg-ocean-800/60 rounded w-2/3" />
        <div className="h-6 bg-ocean-800/60 rounded w-1/3" />
      </GlassPanel>
    );
  }
  // 'row' variant
  return (
    <div
      aria-hidden
      className={cn(
        'h-2 bg-ocean-800/60 rounded-full w-full animate-pulse',
        className,
      )}
    />
  );
}
